import { isNil } from 'lodash'

import { normalizeFieldKey } from './index'

// Geometry for the connector rail drawn in the gutter left of the field rows.
// Kept pure and separate from the component because the component's job is
// measuring the DOM, which jsdom can't do — this is the part worth testing.

// The gutter is this wide; lanes are laid out right-to-left inside it so a
// line never runs under a row.
export const RAIL_WIDTH = 34
const LANE_SPACING = 8
const LANE_COUNT = 3
const FIRST_LANE_X = 26
const ARROW = 4.5

// Above this many outgoing edges a field is a hub, not a link: its lines would
// fuse into a solid band, so they're dropped and the row's count badge carries
// the information instead.
export const HUB_EDGE_THRESHOLD = 12

export const countOutgoing = (links, fieldKey) =>
  links.filter((link) => normalizeFieldKey(link.source) === normalizeFieldKey(fieldKey)).length

export const isHubSource = (links, fieldKey) => countOutgoing(links, fieldKey) > HUB_EDGE_THRESHOLD

export const typeModifier = (typeKey) => (isNil(typeKey) ? null : typeKey.replace(/_/g, '-'))

// One drawable connector per edge whose endpoints are both on screen.
//
// `anchors` maps a normalized field key to the y of its name row, measured by
// the caller. An edge is skipped when either end is missing (a row scrolled out
// of the DOM, or a link to a field this group doesn't render) or when its
// source is a hub.
//
// `focusedKey` emphasizes the edges touching one field without hiding the rest,
// so hovering explains a row rather than blanking the others out.
//
// `typeKeysBySource` maps a normalized field key to its display type, so a line
// can be coloured like the type pill of the field it reads from.
export const buildRailSegments = ({ anchors, links, focusedKey = null, typeKeysBySource = {} }) => {
  const lanes = {}
  const segments = []

  links.forEach((link) => {
    const source = normalizeFieldKey(link.source)
    const target = normalizeFieldKey(link.target)
    const sourceY = anchors[source]
    const targetY = anchors[target]
    if (isNil(sourceY) || isNil(targetY) || isHubSource(links, link.source)) {
      return
    }

    if (isNil(lanes[source])) {
      lanes[source] = Object.keys(lanes).length
    }
    const x = FIRST_LANE_X - (lanes[source] % LANE_COUNT) * LANE_SPACING
    const focused = isNil(focusedKey)
      ? null
      : source === normalizeFieldKey(focusedKey) || target === normalizeFieldKey(focusedKey)
    const downward = targetY >= sourceY
    const elbow = downward ? targetY - 7 : targetY + 7

    segments.push({
      key: `${link.source}->${link.target}-${link.operator}-${link.compare_value}`,
      lane: lanes[source],
      typeKey: typeKeysBySource[source] ?? null,
      // Solid gates, dashed carries a value. The line keeps its source's type
      // colour either way — the colour says which field, the dash says what the
      // link does, and a row can have both kinds arriving at once.
      isData: isNil(link.operator),
      // An elbow into the row, so the line reads as arriving at the field
      // rather than passing it.
      path: `M${x} ${sourceY} L${x} ${elbow} Q${x} ${targetY} ${x + 7} ${targetY}`,
      arrow: `M${x + ARROW} ${targetY - 3.5}L${x + ARROW + 4.5} ${targetY}L${x + ARROW} ${targetY + 3.5}Z`,
      sourceY,
      x,
      emphasized: focused === true,
      muted: focused === false,
    })
  })

  return segments
}
